import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, FileText } from 'lucide-react'

export interface AddPatientRecordFormData {
  patientId: string
  recordType: string
  title: string
  content: Record<string, any>
}

interface AddPatientRecordModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: AddPatientRecordFormData) => Promise<void>
  patientId: string
  patientName: string
}

interface FormErrors {
  recordType?: string
  title?: string
  content?: string
}

// Record types from the backend
const RECORD_TYPES = [
  { value: 'visit_note', label: 'Visit Note', requiredFields: ['chiefComplaint', 'assessment'] },
  { value: 'prescription', label: 'Prescription', requiredFields: ['medication', 'dosage', 'frequency'] },
  { value: 'lab_order', label: 'Lab Order', requiredFields: ['tests', 'priority'] },
  { value: 'lab_result', label: 'Lab Result', requiredFields: ['tests', 'results'] },
  { value: 'imaging_order', label: 'Imaging Order', requiredFields: ['imagingType', 'bodyPart'] },
  { value: 'imaging_result', label: 'Imaging Result', requiredFields: ['imagingType', 'findings'] },
  { value: 'referral', label: 'Referral', requiredFields: ['referralTo', 'reason'] },
  { value: 'care_plan', label: 'Care Plan', requiredFields: ['goals', 'interventions'] },
  { value: 'document', label: 'Document', requiredFields: ['documentType'] },
]

export function AddPatientRecordModal({
  isOpen,
  onClose,
  onSubmit,
  patientId,
  patientName,
}: AddPatientRecordModalProps) {
  const [formData, setFormData] = useState<AddPatientRecordFormData>({
    patientId,
    recordType: '',
    title: '',
    content: {},
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update patientId when prop changes
  useEffect(() => {
    setFormData((prev) => ({ ...prev, patientId }))
  }, [patientId])

  const selectedRecordType = RECORD_TYPES.find((t) => t.value === formData.recordType)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.recordType) {
      newErrors.recordType = 'Please select a record type'
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    // Validate required content fields based on record type
    if (selectedRecordType) {
      const missingFields = selectedRecordType.requiredFields.filter(
        (field) => !formData.content[field]
      )
      if (missingFields.length > 0) {
        newErrors.content = `Missing required fields: ${missingFields.join(', ')}`
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      // Reset form on success
      setFormData({
        patientId,
        recordType: '',
        title: '',
        content: {},
      })
      setErrors({})
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRecordTypeChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      recordType: value,
      content: {}, // Reset content when type changes
    }))
    if (errors.recordType) {
      setErrors((prev) => ({ ...prev, recordType: undefined }))
    }
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, title: e.target.value }))
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: undefined }))
    }
  }

  const handleContentChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      content: { ...prev.content, [field]: e.target.value },
    }))
    if (errors.content) {
      setErrors((prev) => ({ ...prev, content: undefined }))
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        patientId,
        recordType: '',
        title: '',
        content: {},
      })
      setErrors({})
      onClose()
    }
  }

  // Render content fields based on record type
  const renderContentFields = () => {
    if (!selectedRecordType) return null

    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700">Record Details</Label>
        {selectedRecordType.requiredFields.map((field) => (
          <div key={field} className="space-y-1">
            <Label htmlFor={field} className="text-sm capitalize">
              {field.replace(/([A-Z])/g, ' $1').trim()} <span className="text-red-500">*</span>
            </Label>
            {field === 'assessment' || field === 'findings' || field === 'results' || field === 'goals' || field === 'interventions' ? (
              <textarea
                id={field}
                value={formData.content[field] || ''}
                onChange={handleContentChange(field)}
                placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#D64463]/20 focus:border-[#D64463]"
                disabled={isSubmitting}
              />
            ) : (
              <Input
                id={field}
                value={formData.content[field] || ''}
                onChange={handleContentChange(field)}
                placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                disabled={isSubmitting}
              />
            )}
          </div>
        ))}
        {errors.content && <p className="text-sm text-red-500">{errors.content}</p>}
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#D64463]" />
            Add Record for {patientName}
          </DialogTitle>
          <DialogDescription>
            Create a new health record for this patient.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recordType">
              Record Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.recordType}
              onValueChange={handleRecordTypeChange}
              disabled={isSubmitting}
            >
              <SelectTrigger className={errors.recordType ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select record type" />
              </SelectTrigger>
              <SelectContent>
                {RECORD_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.recordType && (
              <p className="text-sm text-red-500">{errors.recordType}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder="Enter record title"
              className={errors.title ? 'border-red-500' : ''}
              disabled={isSubmitting}
            />
            {errors.title && (
              <p className="text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          {renderContentFields()}

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#D64463] hover:bg-[#0F172A] text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Create Record
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddPatientRecordModal
